<script setup lang="ts" generic="T">
import { ref, computed } from "vue";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
} from "@heroicons/vue/outline";
import KekkleEmptyState from "./KekkleEmptyState.vue";

type Props = {
  items: T[];
  title?: string;
  badgeCount?: number;
  emptyText?: string;
  variant?: "default" | "yellow" | "blue" | "purple" | "red";
  cardWidthClass?: string;
};

const {
  items,
  title = "",
  badgeCount = undefined,
  emptyText = "No items available",
  variant = "default",
  cardWidthClass = "w-[88%] sm:w-[450px]",
} = defineProps<Props>();

const carouselRef = ref<HTMLDivElement | null>(null);
const currentIndex = ref(0);

const totalItems = computed(() => items.length);

function scrollLeft(): void {
  if (!carouselRef.value) return;
  const cardWidth = carouselRef.value.clientWidth;
  carouselRef.value.scrollBy({ left: -cardWidth * 0.85, behavior: "smooth" });
}

function scrollRight(): void {
  if (!carouselRef.value) return;
  const cardWidth = carouselRef.value.clientWidth;
  carouselRef.value.scrollBy({ left: cardWidth * 0.85, behavior: "smooth" });
}

function scrollToCard(index: number): void {
  if (!carouselRef.value) return;
  const cards = carouselRef.value.children;
  if (cards[index]) {
    cards[index].scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }
}

function onScroll(): void {
  if (!carouselRef.value || totalItems.value === 0) return;
  const scrollLeftPos = carouselRef.value.scrollLeft;
  const cardWidth = carouselRef.value.children[0]?.clientWidth || 320;
  const newIndex = Math.round(scrollLeftPos / cardWidth);
  if (newIndex >= 0 && newIndex < totalItems.value) {
    currentIndex.value = newIndex;
  }
}

function getHeaderBadgeClasses(): string {
  switch (variant) {
    case "yellow":
      return "border-kekkle-yellow-dark bg-kekkle-yellow-light text-kekkle-dark";
    case "blue":
      return "border-kekkle-blue-dark bg-kekkle-blue-light text-kekkle-dark";
    case "purple":
      return "border-kekkle-purple-dark bg-kekkle-purple-light text-kekkle-dark";
    case "red":
      return "border-kekkle-red-dark bg-kekkle-red-light text-kekkle-dark";
    case "default":
    default:
      return "border-kekkle-purple-dark bg-kekkle-purple-light text-kekkle-dark";
  }
}

function getDotActiveBgClass(): string {
  switch (variant) {
    case "yellow":
      return "bg-kekkle-yellow";
    case "blue":
      return "bg-kekkle-blue";
    case "purple":
      return "bg-kekkle-purple";
    case "red":
      return "bg-kekkle-red";
    case "default":
    default:
      return "bg-kekkle-purple";
  }
}
</script>

<template>
  <div class="w-full">
    <!-- Header with Title, Badge & Navigation Controls -->
    <div
      v-if="title || $slots.header || $slots.icon"
      class="mb-3 flex items-center justify-between gap-4 px-1"
    >
      <slot name="header">
        <div class="flex items-center gap-2">
          <div
            v-if="$slots.icon || variant !== 'default'"
            class="flex h-7 w-7 items-center justify-center rounded-lg border-2"
            :class="getHeaderBadgeClasses()"
          >
            <slot name="icon">
              <SparklesIcon class="h-4 w-4" />
            </slot>
          </div>
          <h2
            v-if="title"
            class="text-lg font-bold text-kekkle-dark dark:text-slate-100"
          >
            {{ title }}
          </h2>
          <slot name="badge">
            <span
              v-if="badgeCount !== undefined"
              class="rounded-full border-2 px-2.5 py-0.5 text-xs font-black"
              :class="getHeaderBadgeClasses()"
            >
              {{ badgeCount }}
            </span>
          </slot>
        </div>
      </slot>

      <!-- Arrow Buttons (Desktop/Tablet) -->
      <div v-if="totalItems > 1" class="flex items-center gap-1.5">
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-lg border-2 border-kekkle-purple bg-white text-kekkle-purple shadow-purple-hard-sm transition-all active:translate-y-0.5 active:shadow-none disabled:opacity-30 dark:border-kekkle-purple dark:bg-kekkle-dark-light dark:text-purple-300"
          :disabled="currentIndex === 0"
          @click="scrollLeft"
        >
          <ChevronLeftIcon class="h-4 w-4 stroke-3" />
        </button>
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-lg border-2 border-kekkle-purple bg-white text-kekkle-purple shadow-purple-hard-sm transition-all active:translate-y-0.5 active:shadow-none disabled:opacity-30 dark:border-kekkle-purple dark:bg-kekkle-dark-light dark:text-purple-300"
          :disabled="currentIndex >= totalItems - 1"
          @click="scrollRight"
        >
          <ChevronRightIcon class="h-4 w-4 stroke-3" />
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <slot v-if="totalItems === 0" name="empty">
      <KekkleEmptyState emoji="💬" :description="emptyText" />
    </slot>

    <!-- Carousel Cards Container -->
    <div v-else class="relative">
      <div
        ref="carouselRef"
        class="scrollbar-hide flex w-full snap-x snap-mandatory gap-4 overflow-x-auto pt-1 pb-4"
        @scroll="onScroll"
      >
        <div
          v-for="(item, index) in items"
          :key="index"
          class="shrink-0 snap-center"
          :class="cardWidthClass"
        >
          <slot name="item" :item="item" :index="index"></slot>
        </div>
      </div>

      <!-- Pagination Dots & Counter Bar -->
      <div
        v-if="totalItems > 1"
        class="mt-3 flex items-center justify-center gap-3"
      >
        <div class="flex items-center gap-1.5">
          <button
            v-for="(_, idx) in items"
            :key="idx"
            type="button"
            class="h-2 rounded-full transition-all duration-300"
            :class="
              idx === currentIndex
                ? ['w-6', getDotActiveBgClass()]
                : 'w-2 bg-gray-300 dark:bg-slate-700'
            "
            @click="scrollToCard(idx)"
          ></button>
        </div>
        <span class="ml-4 text-xs font-bold text-gray-400 dark:text-slate-500">
          {{ currentIndex + 1 }} / {{ totalItems }}
        </span>
      </div>
    </div>
  </div>
</template>
